import { useState, useEffect } from "react";
import { useDomain } from "../../hooks/useDomain";
import { getUserData } from "../../../infrastructure/storage/tokenStorage";
import { MdDns, MdPayment } from "react-icons/md";
import { FiEdit2, FiSave, FiX } from "react-icons/fi";

const DomainNameSection = () => {
  const {
    getAllVmsWithPublicIp,
    updateVmDomainName,
    updateVmBillingType,
    vms,
    loading,
    error,
    successMessage,
  } = useDomain();

  const [userRole, setUserRole] = useState(null);
  const [editingVmId, setEditingVmId] = useState(null);
  const [editDomainValue, setEditDomainValue] = useState("");
  const [editingBillingVmId, setEditingBillingVmId] = useState(null);
  const [editBillingValue, setEditBillingValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingBilling, setIsSavingBilling] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localSuccess, setLocalSuccess] = useState(null);

  useEffect(() => {
    const userData = getUserData();
    setUserRole(userData?.role);
    getAllVmsWithPublicIp();
  }, []);

  useEffect(() => {
    if (successMessage) {
      setLocalSuccess(successMessage);
      setTimeout(() => setLocalSuccess(null), 3000);
    }
    if (error) {
      setLocalError(error);
      setTimeout(() => setLocalError(null), 3000);
    }
  }, [successMessage, error]);

  const canUpdate = userRole === "SUPER_ADMIN" || userRole === "MANAGER";

  const handleEditClick = (vm) => {
    if (!canUpdate) return;
    setEditingVmId(vm.id);
    setEditDomainValue(vm.domainName || "");
    setLocalError(null);
  };

  const handleCancelEdit = () => {
    setEditingVmId(null);
    setEditDomainValue("");
    setLocalError(null);
  };

  const handleSaveDomain = async (vmId) => {
    if (!canUpdate) return;
    if (!editDomainValue.trim()) {
      setLocalError("Domain name cannot be empty");
      return;
    }

    setIsSaving(true);
    setLocalError(null);

    try {
      const result = await updateVmDomainName(vmId, editDomainValue.trim());
      if (result.success) {
        setEditingVmId(null);
        setEditDomainValue("");
        await getAllVmsWithPublicIp();
        setLocalSuccess(result.message);
        setTimeout(() => setLocalSuccess(null), 3000);
      } else {
        setLocalError(result.message);
      }
    } catch (err) {
      setLocalError(err.message || "Failed to update domain name");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBillingEditClick = (vm) => {
    if (!canUpdate) return;
    setEditingBillingVmId(vm.id);
    setEditBillingValue(vm.billingType || "PAYG");
    setLocalError(null);
  };

  const handleCancelBillingEdit = () => {
    setEditingBillingVmId(null);
    setEditBillingValue("");
    setLocalError(null);
  };

  const handleSaveBillingType = async (vmId) => {
    if (!canUpdate) return;
    if (!editBillingValue) {
      setLocalError("Billing type cannot be empty");
      return;
    }

    setIsSavingBilling(true);
    setLocalError(null);

    try {
      const result = await updateVmBillingType(vmId, editBillingValue);
      if (result.success) {
        setEditingBillingVmId(null);
        setEditBillingValue("");
        await getAllVmsWithPublicIp();
        setLocalSuccess(result.message);
        setTimeout(() => setLocalSuccess(null), 3000);
      } else {
        setLocalError(result.message);
      }
    } catch (err) {
      setLocalError(err.message || "Failed to update billing type");
    } finally {
      setIsSavingBilling(false);
    }
  };

  const getBillingTypeBadge = (billingType) => {
    const isReservation = billingType === "RESERVATION";
    return (
      <span
        className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium ${
          isReservation
            ? "bg-green-500/20 text-green-400 border border-green-500"
            : "bg-yellow-500/20 text-yellow-400 border border-yellow-500"
        }`}
      >
        {billingType || "PAYG"}
      </span>
    );
  };

  if (loading && !vms) {
    return (
      <div className="py-4 sm:py-6">
        <div className="bg-primary-dark rounded-xl sm:rounded-2xl p-4 sm:p-6">
          <div className="text-center py-6 sm:py-8">
            <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 text-sm sm:text-base mt-2 sm:mt-3">Loading VMs...</p>
          </div>
        </div>
      </div>
    );
  }

  const vmList = vms || [];

  return (
    <div className="py-4 sm:py-6">
      <div className="bg-primary-dark rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-6 pb-2 sm:pb-3 border-b border-gray-700">
          <MdDns className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">
            VM Domain & Billing
          </h3>
          {canUpdate && (
            <span className="ml-auto text-[10px] sm:text-xs text-green-400">
              ✓ You have edit permissions
            </span>
          )}
          {!canUpdate && userRole && (
            <span className="ml-auto text-[10px] sm:text-xs text-red-400">
              🔒 Read only mode
            </span>
          )}
        </div>

        {(localError || error) && (
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
            <p className="text-red-400 text-xs sm:text-sm">{localError || error}</p>
          </div>
        )}

        {localSuccess && (
          <div className="bg-green-500/20 border border-green-500 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
            <p className="text-green-400 text-xs sm:text-sm">{localSuccess}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium">VM Name</th>
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium hidden sm:table-cell">IP</th>
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium">Domain</th>
                <th className="text-left py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium">Billing</th>
                <th className="text-center py-2 sm:py-3 px-1 sm:px-2 text-gray-400 font-medium">Actions</th>
               </tr>
            </thead>
            <tbody>
              {vmList.map((vm) => (
                <tr
                  key={vm.id}
                  className="border-b border-gray-700/50 hover:bg-white/5 transition"
                >
                  <td className="py-2 sm:py-3 px-1 sm:px-2">
                    <span className="text-white font-medium text-xs sm:text-sm break-words">
                      {vm.vmName}
                    </span>
                  </td>
                  <td className="py-2 sm:py-3 px-1 sm:px-2 hidden sm:table-cell">
                    <code className="bg-gray-800 px-1 sm:px-2 py-0.5 sm:py-1 rounded text-primary-cyan font-mono text-[10px] sm:text-xs break-all">
                      {vm.ipAddress || "No Public IP"}
                    </code>
                  </td>
                  <td className="py-2 sm:py-3 px-1 sm:px-2">
                    {editingVmId === vm.id && canUpdate ? (
                      <input
                        type="text"
                        value={editDomainValue}
                        onChange={(e) => setEditDomainValue(e.target.value)}
                        className="w-full bg-gray-800 border border-primary-cyan rounded-lg px-2 sm:px-3 py-1 text-white text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-primary-cyan"
                        placeholder="Enter domain"
                        autoFocus
                      />
                    ) : (
                      <span className="text-gray-300 text-xs sm:text-sm break-words">
                        {vm.domainName || (
                          <span className="text-gray-500 italic">Not set</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="py-2 sm:py-3 px-1 sm:px-2">
                    {editingBillingVmId === vm.id && canUpdate ? (
                      <select
                        value={editBillingValue}
                        onChange={(e) => setEditBillingValue(e.target.value)}
                        className="w-full bg-gray-800 border border-primary-cyan rounded-lg px-2 sm:px-3 py-1 text-white text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-primary-cyan"
                      >
                        <option value="PAYG">PAYG</option>
                        <option value="RESERVATION">RESERVATION</option>
                      </select>
                    ) : (
                      getBillingTypeBadge(vm.billingType)
                    )}
                  </td>
                  <td className="py-2 sm:py-3 px-1 sm:px-2">
                    {canUpdate ? (
                      <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                        {editingVmId === vm.id ? (
                          <>
                            <button
                              onClick={() => handleSaveDomain(vm.id)}
                              disabled={isSaving}
                              className="text-green-500 hover:text-green-400 transition p-1"
                              title="Save domain"
                            >
                              {isSaving ? (
                                <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <FiSave className="w-3 h-3 sm:w-4 sm:h-4" />
                              )}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-red-500 hover:text-red-400 transition p-1"
                              title="Cancel"
                            >
                              <FiX className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEditClick(vm)}
                            className="text-primary-cyan hover:text-primary-cyan/80 transition p-1"
                            title="Edit domain"
                          >
                            <FiEdit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        )}

                        {editingBillingVmId === vm.id ? (
                          <>
                            <button
                              onClick={() => handleSaveBillingType(vm.id)}
                              disabled={isSavingBilling}
                              className="text-green-500 hover:text-green-400 transition p-1"
                              title="Save billing"
                            >
                              {isSavingBilling ? (
                                <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <FiSave className="w-3 h-3 sm:w-4 sm:h-4" />
                              )}
                            </button>
                            <button
                              onClick={handleCancelBillingEdit}
                              className="text-red-500 hover:text-red-400 transition p-1"
                              title="Cancel"
                            >
                              <FiX className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleBillingEditClick(vm)}
                            className="text-purple-500 hover:text-purple-400 transition p-1"
                            title="Edit billing"
                          >
                            <MdPayment className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-[10px] sm:text-xs italic">Read only</span>
                    )}
                  </td>
                 </tr>
              ))}
            </tbody>
           </table>
        </div>

        {vmList.length === 0 && !loading && (
          <div className="text-center py-6 sm:py-8">
            <p className="text-gray-400 text-sm sm:text-base">No VMs found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainNameSection;